import React, { useState } from 'react';
import {
    Box,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    TableBody,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { UseQueryResult } from '@tanstack/react-query';
import VideoCommentTableSkeleton from './VideoCommentTableSkeleton';
import { PaginatedTable } from 'ui';
import { components } from 'apis';
import { useApi } from 'hooks';
type Comment = components['schemas']['Comment'];
type GetCommentsResponse = components['schemas']['GetCommentsResponse'];

type Order = 'asc' | 'desc';

interface Data {
    User: string;
    Comment: string;
    Likes: string;
    Dislikes: string;
    Replies: string;
    CreatedOn: string;
}

interface HeadCell {
    disablePadding: boolean;
    id: keyof Data;
    label: string;
    numeric: boolean;
}

const headers: HeadCell[] = [
    {
        id: 'User',
        label: 'User',
        numeric: false,
        disablePadding: true,
    },
    {
        id: 'Comment',
        label: 'Comment',
        numeric: false,
        disablePadding: true,
    },
    {
        id: 'Likes',
        label: 'Likes',
        numeric: false,
        disablePadding: true,
    },
    {
        id: 'Dislikes',
        label: 'Dislikes',
        numeric: false,
        disablePadding: true,
    },
    {
        id: 'Replies',
        label: 'Replies',
        numeric: false,
        disablePadding: true,
    },
    {
        id: 'CreatedOn',
        label: 'Date',
        numeric: false,
        disablePadding: true,
    },
];

interface VideoEditTableHeadProps {
    orderBy: keyof Data;
    order: Order;
}

const VideoCommentsTableHead: React.FC<VideoEditTableHeadProps> = (props) => {
    return (
        <TableHead>
            <TableRow>
                {headers.map((header) => (
                    <TableCell
                        key={header.id}
                        align={header.numeric ? 'right' : 'left'}
                        padding={header.disablePadding ? 'none' : 'normal'}
                        sortDirection={
                            props.orderBy === header.id ? props.order : false
                        }
                    >
                        <TableSortLabel
                            active={props.orderBy === header.id}
                            direction={
                                props.orderBy === header.id
                                    ? props.order
                                    : 'asc'
                            }
                        >
                            {header.label}
                            <Box component="span" sx={visuallyHidden}>
                                {props.order === 'desc'
                                    ? 'sorted descending'
                                    : 'sorted ascending'}
                            </Box>
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

interface VideoCommentsTableProps {
    videoId: string;
}

const VideoCommentsTableBody: React.FC<{ comments: Comment[] }> = (props) => {
    return (
        <TableBody>
            {props.comments.map((comment) => (
                <TableRow key={comment.id.toString()}>
                    <TableCell
                        component="th"
                        id={comment.id.toString()}
                        scope="row"
                        padding="none"
                    >
                        {comment.channelName}
                    </TableCell>
                    <TableCell
                        component="th"
                        id={comment.id.toString()}
                        scope="row"
                        padding="none"
                    >
                        {comment.body}
                    </TableCell>
                    <TableCell
                        component="th"
                        id={comment.id.toString()}
                        scope="row"
                        padding="none"
                    >
                        {comment.likes}
                    </TableCell>
                    <TableCell
                        component="th"
                        id={comment.id.toString()}
                        scope="row"
                        padding="none"
                    >
                        {comment.dislikes}
                    </TableCell>
                    <TableCell
                        component="th"
                        id={comment.id.toString()}
                        scope="row"
                        padding="none"
                    >
                        {comment.replies}
                    </TableCell>
                    <TableCell
                        component="th"
                        id={comment.id.toString()}
                        scope="row"
                        padding="none"
                    >
                        {comment.createdAt.toString()}
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    );
};

const VideoCommentsTable: React.FC<VideoCommentsTableProps> = (props) => {
    const [page, setPage] = useState<number>(0);
    const [limit, setLimit] = useState<number>(10);
    const { api } = useApi();
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };
    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage);
    };
    const commentsForChannel: UseQueryResult<GetCommentsResponse> =
        api.useQuery(
            'get',
            `/comments/${props.videoId}?page=${page}&limit=${limit}`,
            {},
            {
                refetchInterval: false,
                refetchOnMount: false,
                refetchOnWindowFocus: false,
            },
        );
    return (
        <PaginatedTable
            isLoading={commentsForChannel.isLoading}
            isError={commentsForChannel.isError}
            data={commentsForChannel.data?.documents ?? []}
            count={commentsForChannel?.data?.totalCount ?? 0}
            header={() => (
                <VideoCommentsTableHead orderBy={'User'} order={'asc'} />
            )}
            body={() => (
                <VideoCommentsTableBody
                    comments={commentsForChannel.data?.documents ?? []}
                />
            )}
            loadingSkeleton={VideoCommentTableSkeleton}
            limit={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
        />
    );
};

export default VideoCommentsTable;
